import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { NIVELES } from './useZoom'

// Zoom in/out for the OT board and list: a level of 50-150 % kept per view in the browser, a small control to
// change it, and Ctrl + mouse wheel over the content. It scales with CSS `zoom` on the content, so the layout
// follows (a narrower board needs less sideways scrolling) and the scroll bars stay the real, unzoomed ones.

export function ControlZoom({ z }) {
  const { t } = useTranslation('operations')
  const btn = 'w-7 h-7 flex items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent'
  return (
    <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white p-0.5" role="group" aria-label={t('ot.zoom.titulo')}>
      <button type="button" onClick={z.alejar} disabled={z.zoom <= NIVELES[0]} className={btn} title={t('ot.zoom.alejar')} aria-label={t('ot.zoom.alejar')}>−</button>
      <button type="button" onClick={z.restablecer} disabled={z.zoom === 100} title={t('ot.zoom.restablecer')} aria-label={t('ot.zoom.restablecer')}
        className="min-w-[3rem] h-7 px-1 rounded-md text-xs font-medium tabular-nums text-gray-700 hover:bg-gray-100 disabled:hover:bg-transparent">{z.zoom}%</button>
      <button type="button" onClick={z.acercar} disabled={z.zoom >= NIVELES[NIVELES.length - 1]} className={btn} title={t('ot.zoom.acercar')} aria-label={t('ot.zoom.acercar')}>+</button>
      <button type="button" onClick={z.ajustar} title={t('ot.zoom.ajustarAyuda')} aria-label={t('ot.zoom.ajustarAyuda')}
        className="h-7 px-2 ml-0.5 rounded-md text-xs font-medium text-gray-600 hover:bg-gray-100">⤢ {t('ot.zoom.ajustar')}</button>
    </div>
  )
}

// The content, zoomed. With `ajustarAlto` the box takes the screen's height from where it starts, so its sideways
// scroll bar is always in sight at the bottom and the board scrolls inside it; without it the box grows with the content.
export function ZonaZoom({ z, ajustarAlto = false, libre = false, children, className = '' }) {
  const { acercar, alejar, zonaRef } = z
  const ref = useRef(null)
  const fijar = useCallback((el) => { ref.current = el; zonaRef.current = el }, [zonaRef])      // the hook measures it for "Ajustar"
  const [alto, setAlto] = useState(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    // Ctrl + wheel zooms the content instead of the whole page.
    const rueda = (e) => {
      if (!e.ctrlKey) return
      e.preventDefault()
      if (e.deltaY < 0) acercar(); else if (e.deltaY > 0) alejar()
    }
    el.addEventListener('wheel', rueda, { passive: false })
    return () => el.removeEventListener('wheel', rueda)
  }, [acercar, alejar])

  useEffect(() => {
    if (!ajustarAlto) return undefined
    const medir = () => {
      const el = ref.current
      if (!el) return
      const top = el.getBoundingClientRect().top
      setAlto(Math.max(280, Math.round(window.innerHeight - top - 16)))
    }
    const ro = new ResizeObserver(medir)             // fires once on observe, and whenever the page above changes size
    ro.observe(document.body)
    window.addEventListener('resize', medir)
    return () => { ro.disconnect(); window.removeEventListener('resize', medir) }
  }, [ajustarAlto])

  return (
    <div ref={fijar} className={`${libre ? "" : "overflow-auto"} ${className}`} style={ajustarAlto && alto ? { height: alto } : undefined}>
      <div style={{ zoom: z.zoom / 100 }}>{children}</div>
    </div>
  )
}
